import {
  Box,
  Card,
  Image,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
  useMantineTheme
} from '@mantine/core'
import { useHover } from '@mantine/hooks'
import { IconTrash } from '@tabler/icons-react'
import { MouseEvent, MouseEventHandler } from 'react'
import { FormattedMessage } from 'react-intl'
import ImagePlaceholder from '@/assets/image-placeholder.png'
import { CameraSourceBadge } from '@/components/cameras/CameraBadges/CameraSourceBadge'
import { CameraTypeBadge } from '@/components/cameras/CameraBadges/CameraTypeBadge'
import { Camera, CameraSource } from '@/types/camera'
import { OnlineStatus } from '../../../../devices/OnlineStatus/OnlineStatus'
import { SelectableCard } from '../../../../ui-shared/SelectableCard/SelectableCard'

const IMAGE_HEIGHT = 120

type RemoveIconProps = {
  onClick: MouseEventHandler<HTMLDivElement>
}

const RemoveIcon = ({ onClick }: RemoveIconProps) => {
  const { hovered, ref } = useHover()

  const iconColor = hovered
    ? 'var(--mantine-color-red-8)'
    : 'var(--mantine-color-gray-8)'

  return (
    <Tooltip label={<FormattedMessage id="cameras.delete" />}>
      <ThemeIcon ref={ref} variant="subtle" size="sm" onClick={onClick}>
        <IconTrash color={iconColor} style={{ width: '70%', height: '70%' }} />
      </ThemeIcon>
    </Tooltip>
  )
}

type SelectableCameraCardProps = {
  name: string
  thumbnailUrl: string
  source: CameraSource
  streamType?: Camera['stream_type']
  isActive: boolean
  isSelected: boolean
  disabled: boolean
  showRemoveIcon?: boolean
  onClick: () => void
  onRemoveClick: () => void
}

export const SelectableCameraCard = ({
  name,
  thumbnailUrl,
  source,
  streamType,
  isActive,
  isSelected,
  disabled,
  showRemoveIcon,
  onClick,
  onRemoveClick
}: SelectableCameraCardProps) => {
  const theme = useMantineTheme()

  const handleRemoveClick = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation()
    onRemoveClick()
  }

  return (
    <SelectableCard
      isSelected={isSelected}
      disabled={disabled}
      height={theme.other.deploymentCardHeight}
      onClick={onClick}
    >
      <Card.Section pos="relative" withBorder>
        <Image
          h={IMAGE_HEIGHT}
          fit="cover"
          src={thumbnailUrl}
          fallbackSrc={ImagePlaceholder}
        />

        {streamType === 'usb' && (
          <Tooltip
            label={<FormattedMessage id="cameras.type.usb.ownContainer" />}
            w={260}
            multiline
          >
            <Box pos="absolute" top={0} left={8}>
              <CameraTypeBadge type={streamType} />
            </Box>
          </Tooltip>
        )}

        <Box pos="absolute" top={0} right={8}>
          <CameraSourceBadge source={source} />
        </Box>
      </Card.Section>

      <Card.Section pos="relative" py="sm" inheritPadding>
        <Stack align="center" gap="xs">
          <Text ta="center" size="sm" fw="bold" lineClamp={2}>
            {name}
          </Text>

          <OnlineStatus status={isActive ? 'online' : 'offline'} />
        </Stack>

        {showRemoveIcon && (
          <Box pos="absolute" top={0} right={0}>
            <RemoveIcon onClick={handleRemoveClick} />
          </Box>
        )}
      </Card.Section>
    </SelectableCard>
  )
}
